'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import { isDefined } from '@morpho-org/morpho-ts';
import { CursorPagination, TablePropertiesPopover } from '@morpho-org/ui-v6/client';
import { TableNoResults } from '@morpho-org/ui-v6/server';
import { useTable } from '@tanstack/react-table';
import { startTransition, useState } from 'react';
import { DateRangeFilter, dateRangeToTimestamps, type DateRangeValue } from '@/components/common/DateRangeFilter';
import { ErrorDataUnavailable } from '@/components/common/Error';
import { Table, TrBodyRow, TrHeaderGroup } from '@/components/common/Table';
import { TableScrollArea } from '@/components/common/Table/CompactTable/Table';
import { TransactionTypeFilter } from '@/components/common/TransactionTypeFilter';
import useTableSettings from '@/helpers/cookieStorage/userSettings/tableSettings/client';
import { PageKey } from '@/helpers/cookieStorage/userSettings/types';
import { useSkeletonRows } from '@/hooks/useSkeletonRows';
import { TransactionType } from '@/types/legacy';
import { MARKET_TRANSACTIONS_DEFAULT_TABLE_SETTINGS, MARKET_TRANSACTIONS_STORAGE_KEY, PAGE_SIZE, TRANSACTION_FILTER_OPTIONS } from './config';
import { COLUMN_LABELS, columnsDefs, marketTransactionsTableFeatures } from './config/MarketTransactions.columns';
import useTableTransactionData from './hooks/useTableTransactionData';
import useTableTransactionRow from './hooks/useTableTransactionRow';
import MarketTransactionsLayout from './MarketTransactionsLayout';
export interface MarketTransactionsClientProps {
  chainId: ChainId;
  marketId: string;
}
const MarketTransactionsClient = t0 => {
  const $ = _c(78);
  const {
    chainId,
    marketId
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [transactionTypeFilter, setTransactionTypeFilter] = useState(t1);
  const [dateRange, setDateRange] = useState();
  let t2;
  if ($[1] !== dateRange) {
    t2 = dateRangeToTimestamps(dateRange);
    $[1] = dateRange;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    timestampGte,
    timestampLte
  } = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      pageKey: PageKey.Market,
      tableSettingsObjectKey: MARKET_TRANSACTIONS_STORAGE_KEY,
      defaultTableSettings: MARKET_TRANSACTIONS_DEFAULT_TABLE_SETTINGS
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const {
    columnVisibility,
    setColumnVisibility
  } = useTableSettings(t3);
  let t4;
  if ($[4] !== transactionTypeFilter) {
    t4 = transactionTypeFilter.map(_temp).filter(isDefined);
    $[4] = transactionTypeFilter;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const activeTransactionTypes = t4;
  let t5;
  if ($[6] !== activeTransactionTypes || $[7] !== chainId || $[8] !== marketId || $[9] !== timestampGte || $[10] !== timestampLte) {
    t5 = {
      chainId,
      marketId,
      first: PAGE_SIZE,
      filteredTransactionTypes: activeTransactionTypes,
      timestampGte,
      timestampLte
    };
    $[6] = activeTransactionTypes;
    $[7] = chainId;
    $[8] = marketId;
    $[9] = timestampGte;
    $[10] = timestampLte;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const {
    items: transactions,
    partial,
    isLoading,
    isLoadingNextPage,
    error,
    pageIndex,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    prefetchNext
  } = useTableTransactionData(t5);
  let t6;
  if ($[12] !== chainId || $[13] !== transactions) {
    t6 = {
      transactions,
      chainId
    };
    $[12] = chainId;
    $[13] = transactions;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const formattedTransactions = useTableTransactionRow(t6);
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = selectedTransactionTypes => {
      startTransition(() => {
        setTransactionTypeFilter(selectedTransactionTypes);
      });
    };
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const handleFilterChange = t7;
  let t8;
  if ($[16] !== columnVisibility) {
    t8 = {
      columnVisibility
    };
    $[16] = columnVisibility;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== formattedTransactions || $[19] !== setColumnVisibility || $[20] !== t8) {
    t9 = {
      features: marketTransactionsTableFeatures,
      data: formattedTransactions,
      state: t8,
      columns: columnsDefs,
      enableSorting: false,
      onColumnVisibilityChange: setColumnVisibility
    };
    $[18] = formattedTransactions;
    $[19] = setColumnVisibility;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const transactionsTable = useTable(t9);
  const loading = isLoading || isLoadingNextPage;
  let t10;
  if ($[22] !== loading || $[23] !== transactionsTable) {
    t10 = !loading && transactionsTable.getRowModel().rows.length === 0;
    $[22] = loading;
    $[23] = transactionsTable;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const hasNoTransactions = t10;
  let t11;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      first: PAGE_SIZE,
      totalCount: PAGE_SIZE,
      pagination: {
        pageIndex: 0,
        pageSize: PAGE_SIZE
      },
      columnsDefs
    };
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const loadingRows = useSkeletonRows(t11);
  let t12;
  if ($[26] !== columnVisibility || $[27] !== transactionsTable) {
    let t13;
    if ($[29] !== columnVisibility) {
      t13 = (column, index) => {
        const label = COLUMN_LABELS[column.id as keyof typeof COLUMN_LABELS];
        if (!isDefined(label)) {
          return null;
        }
        return {
          label,
          value: column.id,
          isActive: isDefined(columnVisibility[column.id]) ? !!columnVisibility[column.id] : true,
          isDisabled: index === 0,
          action: () => {
            column.toggleVisibility();
          }
        };
      };
      $[29] = columnVisibility;
      $[30] = t13;
    } else {
      t13 = $[30];
    }
    t12 = transactionsTable.getAllLeafColumns().map(t13).filter(isDefined);
    $[26] = columnVisibility;
    $[27] = transactionsTable;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const columnOptions = t12;
  let t13;
  if ($[31] !== dateRange) {
    t13 = <DateRangeFilter value={dateRange} onChange={setDateRange} side="bottom" align="end" page="market_activity" />;
    $[31] = dateRange;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <span className="text-3xs text-foreground lg:hidden">Type:</span>;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== transactionTypeFilter) {
    t15 = <div className="gap-xs flex flex-row items-center">{t14}<TransactionTypeFilter selectedTypes={transactionTypeFilter} onFilterChange={handleFilterChange} options={TRANSACTION_FILTER_OPTIONS} page="market_activity" /></div>;
    $[34] = transactionTypeFilter;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== columnOptions) {
    t16 = <TablePropertiesPopover options={columnOptions} />;
    $[36] = columnOptions;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t13 || $[39] !== t15 || $[40] !== t16) {
    t17 = <div className="gap-sm max-lg:gap-xs flex flex-row items-center">{t13}{t15}{t16}</div>;
    $[38] = t13;
    $[39] = t15;
    $[40] = t16;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== hasNoTransactions || $[43] !== loading || $[44] !== loadingRows || $[45] !== transactionsTable) {
    t18 = (!hasNoTransactions || loading) && <Table.Table tableVariant="compact"><Table.Thead>{transactionsTable.getHeaderGroups().map(_temp2)}</Table.Thead><Table.Tbody>{!loading && transactionsTable.getRowModel().rows.map(_temp3)}{loading && loadingRows.map(_temp4)}</Table.Tbody></Table.Table>;
    $[42] = hasNoTransactions;
    $[43] = loading;
    $[44] = loadingRows;
    $[45] = transactionsTable;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== error || $[48] !== hasNoTransactions || $[49] !== loading) {
    t19 = hasNoTransactions && !error && !loading && <TableNoResults text="No transactions found." height="478px" style={{
      borderRadius: "0"
    }} />;
    $[47] = error;
    $[48] = hasNoTransactions;
    $[49] = loading;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  let t20;
  if ($[51] !== error || $[52] !== loading) {
    t20 = !loading && error && <ErrorDataUnavailable minHeight="340px" style={{
      borderRadius: "0"
    }} />;
    $[51] = error;
    $[52] = loading;
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  let t21;
  if ($[54] !== t18 || $[55] !== t19 || $[56] !== t20) {
    t21 = <TableScrollArea className="grid h-full w-full rounded-[20px]">{t18}{t19}{t20}</TableScrollArea>;
    $[54] = t18;
    $[55] = t19;
    $[56] = t20;
    $[57] = t21;
  } else {
    t21 = $[57];
  }
  let t22;
  if ($[58] !== error || $[59] !== loading || $[60] !== partial) {
    t22 = partial && !loading && !error && <p className="text-secondary-foreground px-sm text-xs" data-testid="market-transactions-partial-notice">Some transactions could not be fully loaded, so this page may be incomplete.</p>;
    $[58] = error;
    $[59] = loading;
    $[60] = partial;
    $[61] = t22;
  } else {
    t22 = $[61];
  }
  let t23;
  if ($[62] !== canNextPage || $[63] !== canPreviousPage || $[64] !== error || $[65] !== loading || $[66] !== nextPage || $[67] !== pageIndex || $[68] !== prefetchNext || $[69] !== previousPage) {
    t23 = <CursorPagination loading={loading} error={error} pageIndex={pageIndex} previousPage={previousPage} nextPage={nextPage} canPreviousPage={canPreviousPage} canNextPage={canNextPage} onPrefetchNext={prefetchNext} />;
    $[62] = canNextPage;
    $[63] = canPreviousPage;
    $[64] = error;
    $[65] = loading;
    $[66] = nextPage;
    $[67] = pageIndex;
    $[68] = prefetchNext;
    $[69] = previousPage;
    $[70] = t23;
  } else {
    t23 = $[70];
  }
  let t24;
  if ($[71] !== t21 || $[72] !== t22 || $[73] !== t23) {
    t24 = <div className="gap-lg flex h-full w-full flex-col"><div className="gap-xs flex flex-col">{t21}{t22}{t23}</div></div>;
    $[71] = t21;
    $[72] = t22;
    $[73] = t23;
    $[74] = t24;
  } else {
    t24 = $[74];
  }
  let t25;
  if ($[75] !== t17 || $[76] !== t24) {
    t25 = <MarketTransactionsLayout tableSettingsSlot={t17}>{t24}</MarketTransactionsLayout>;
    $[75] = t17;
    $[76] = t24;
    $[77] = t25;
  } else {
    t25 = $[77];
  }
  return t25;
};
export default MarketTransactionsClient;
function _temp(t) {
  return TransactionType[t as keyof typeof TransactionType];
}
function _temp2(headerGroup) {
  return <TrHeaderGroup key={headerGroup.id} headerGroup={headerGroup} />;
}
function _temp3(row) {
  return <TrBodyRow key={`${row.id}-${row.original.type}`} row={row} />;
}
function _temp4(row_0, index_0) {
  return <TrBodyRow key={`skeleton-${index_0}`} skeletonRow={row_0} />;
}